import { z } from 'zod';
import type { AssignableOrgRole } from '@hangar/shared';
import { api } from './client';
import { ClientOrgCreated, ClientOrgList, Invitation, InvitationAccepted, InvitationList, Member, MemberList, ProviderSetup, type ClientOrg } from './schemasMembers';

/* ---------- Members, invitations and client orgs (api/openapi.yaml Members, Invitations, Platform) ---------- */

const enc = encodeURIComponent;
const NoContent = z.null();

export interface InviteInput {
  email: string;
  orgRole: AssignableOrgRole;
  name?: string;
  message?: string;
}

export interface ClientOrgInput {
  name: string;
  slug: string;
  firstAdmin: { email: string; name?: string };
  message?: string;
}

/** `GET /members`: the whole org (admins only). */
export const listMembers = (): Promise<Member[]> => api('GET', '/members', { schema: MemberList }).then((p) => p.data);
export const updateMember = (userId: string, body: { role?: AssignableOrgRole; expiresAt?: string | null }): Promise<Member> =>
  api('PATCH', `/members/${enc(userId)}`, { schema: Member, body });
export const removeMember = (userId: string): Promise<null> => api('DELETE', `/members/${enc(userId)}`, { schema: NoContent });

/** `GET /invitations?status=pending`: the open invitations, newest first (one page of 200). */
export const listPendingInvitations = (): Promise<Invitation[]> =>
  api('GET', '/invitations', { schema: InvitationList, query: { status: 'pending', limit: 200 } }).then((p) => p.data);
export const createInvitation = (body: InviteInput, idempotencyKey: string): Promise<Invitation> => api('POST', '/invitations', { schema: Invitation, body, idempotencyKey });
export const resendInvitation = (invitationId: string): Promise<Invitation> => api('POST', `/invitations/${enc(invitationId)}/resend`, { schema: Invitation });
export const revokeInvitation = (invitationId: string): Promise<null> => api('DELETE', `/invitations/${enc(invitationId)}`, { schema: NoContent });
/** The email link: the signed-in invitee joins the org. */
export const acceptInvitation = (token: string): Promise<InvitationAccepted> => api('POST', '/invitations/accept', { schema: InvitationAccepted, body: { token } });
/** The org switcher: the signed-in user joins an org that invited them. */
export const joinInvitation = (invitationId: string): Promise<InvitationAccepted> => api('POST', `/invitations/${enc(invitationId)}/accept`, { schema: InvitationAccepted });

/** `GET /platform/orgs` (platform operators only). */
export const listClientOrgs = (): Promise<ClientOrg[]> => api('GET', '/platform/orgs', { schema: ClientOrgList, query: { limit: 200 } }).then((p) => p.data);
export const createClientOrg = (body: ClientOrgInput): Promise<ClientOrgCreated> => api('POST', '/platform/orgs', { schema: ClientOrgCreated, body });
/** `POST /platform/orgs/{orgId}/providers`: (re)runs the client's EAS and Bitrise setup (platform operators only). */
export const setUpClientProviders = (orgId: string): Promise<ProviderSetup> => api('POST', `/platform/orgs/${encodeURIComponent(orgId)}/providers`, { schema: ProviderSetup });
